import React, { useEffect, useState, useRef } from "react";
import Decimal from "decimal.js";

import { default_borrower } from "../../../schema/borrower";
import CREDIT_SCORE_RANGES from "../../../constants/credit_score";
import RENTING_OR_OWNING, { OWN, RENT } from "../../../constants/renting_or_owning";
import MARITAL_STATUS from "../../../constants/marital_status";
import {
  DISPOSITION_STATUS,
  OCCUPANCY,
  RETAINED,
  INVESTMENT,
  SECONDARY
} from "../../../constants/property_disposition";
import {
  YES,
  YES_NO_TBD,
  YES_NO_TBD_CREDIT_DEBT_OTHER
} from "../../../constants/yes_no";

import CurrencyField from "../../fields/currency_field";
import DateField from "../../fields/date_field";
import NumberField from "../../fields/number_field";
import SelectField from "../../fields/select_field";
import {
  representative_mid_score,
  range_id_for_mid_score,
  borrower_has_credit_score
} from "../../../calculations/applicant";
import TextField from "../../fields/text_field";

import { useDocument } from "../../loan_tools/loan_tool";
import PhoneField from "../../fields/phone_field";
import Section from "../../ui/section";
import { borrower_name } from "../../../constants/utils";
import TrainingTip from "../../ui/training_tip";
import SsnField from "../../fields/ssn_field";

export default function EditBorrower({
  borrower = default_borrower,
  borrowerIndex,
  subjectPropertyIsReo,
  onChangeBorrowers,
  showApplicantTable
}) {
  const [,,,{ saveDocument }] = useDocument();
  const [borrowerEdit, setBorrowerEdit] = useState({
    ...default_borrower,
    ...borrower
  });
  const [showRetainInvestmentNote, setShowRetainInvestmentNote] = useState(false);
  const [document] = useDocument();
  const { borrowers } = document.applicant;
  const callback = useRef();

  // This function updates callback with current state of the subform
  useEffect(() => {
    callback.current = () => {
      if (borrowerEdit.is_modified) {
        if (validBorrower()) {
          if (
            window.confirm(
              "You have unsaved data in Borrower Form. Would you like to save it before you leave?"
            )
          ) {
            save();
          }
        } else {
          // did not pass validation
          alert(
            "Not all necessary fields were entered. Therefore changes would be dismissed."
          );
        }
      }
    };
  }, [borrowerEdit]);

  useEffect(() => {
    // do nothing

    // cleanup
    return () => {
      callback.current();
    };
  }, []);

  function validBorrower() {
    const validationFields = [
      borrowerEdit.first_name,
      borrowerEdit.last_name,
      borrowerEdit.ssn,
      borrowerEdit.date_of_birth
    ];
    const filled = value =>
      value !== "" && value !== undefined && value !== null;
    // Credit requirement (needed for pricing): a VALID Mid Score (0/blank does
    // not count) OR a chosen range. Prevents saving with empty/zero scores.
    return (
      validationFields.every(filled) && borrower_has_credit_score(borrowerEdit)
    );
  }

  function save() {
    delete borrowerEdit.is_modified;
    borrowers[borrowerIndex] = borrowerEdit;

    for (let index in borrowers) {
      const borrower = borrowers[index];
      if (
        borrowerIndex === index ||
        borrower.same_address_as !== borrowerIndex.toString()
      ) {
        continue;
      }
      borrowers[index] = {
        ...borrower,
        current_address: borrowerEdit.current_address,
        current_address_2: borrowerEdit.current_address_2,
        city: borrowerEdit.city,
        state: borrowerEdit.state,
        zip_code: borrowerEdit.zip_code,
        currently_renting_or_owning: borrowerEdit.currently_renting_or_owning,
        years_at_current_residence: borrowerEdit.years_at_current_residence,
        rental_amount: borrowerEdit.rental_amount
      };
    }
    onChangeBorrowers(borrowers);
    saveDocument();
    showApplicantTable();
  }

  function cancel() {
    showApplicantTable();
  }

  function onChangeBorrower(updatedState) {
    setBorrowerEdit({
      ...borrowerEdit,
      ...updatedState,
      is_modified: true
    });
  }

  // Entering/editing a bureau score (Experian/TransUnion/Equifax) recomputes the
  // borrower's representative Mid Score live (median of 3, lower of 2, or the
  // single score) and derives the range bucket pricing uses — so the blue Mid
  // Score populates from manual entry too, not just from a credit-report import.
  function onChangeBureauScore(updatedState) {
    const merged = { ...borrowerEdit, ...updatedState };
    const mid = representative_mid_score([
      merged.credit_score_experian,
      merged.credit_score_transunion,
      merged.credit_score_equifax
    ]);
    const patch = { ...updatedState };
    if (mid != null) {
      patch.credit_mid_score = String(mid);
      const rangeId = range_id_for_mid_score(mid);
      if (rangeId) {
        patch.lowest_credit_mid_score = rangeId;
      }
    } else {
      patch.credit_mid_score = "";
    }
    onChangeBorrower(patch);
  }

  // When the current residence becomes a Retained / Investment property, its
  // full PITI+HOA (needed for the reserve calc) lives on the Selling Property
  // page, not the credit-derived Debt page. Prompt the LO to go fill it in.
  function onChangeResidence(updatedState) {
    const merged = { ...borrowerEdit, ...updatedState };
    // Kept as a rental (Investment) or a second home (Secondary): both now have
    // their own page (Investment / Second Home) and an auto Invest REO row.
    const keptKind = b =>
      b.residence_disposition_status === RETAINED &&
      (b.residence_intended_occupancy === INVESTMENT ||
        b.residence_intended_occupancy === SECONDARY)
        ? b.residence_intended_occupancy
        : null;
    const nowKept = keptKind(merged);
    onChangeBorrower(updatedState);
    if (nowKept && nowKept !== keptKind(borrowerEdit)) {
      setShowRetainInvestmentNote(nowKept);
    }
  }

  function onChangeSameAddressAs(updatedState) {
    if (updatedState.same_address_as === "none") {
      setBorrowerEdit({
        ...borrowerEdit,
        ...updatedState,
        is_modified: true
      });
      return;
    }
    const borrowerIndex = new Decimal(updatedState.same_address_as).toNumber();
    const otherBorrower = borrowers[borrowerIndex];
    if (!otherBorrower) {
      return; // This should never happen.
    }

    const borrower = {
      ...borrowerEdit,
      ...updatedState,
      current_address: otherBorrower.current_address,
      current_address_2: otherBorrower.current_address_2,
      city: otherBorrower.city,
      state: otherBorrower.state,
      zip_code: otherBorrower.zip_code,
      currently_renting_or_owning: otherBorrower.currently_renting_or_owning,
      years_at_current_residence: otherBorrower.years_at_current_residence,
      market_value: otherBorrower.market_value,
      rental_amount: otherBorrower.rental_amount,
      residence_disposition_status: otherBorrower.residence_disposition_status,
      residence_intended_occupancy: otherBorrower.residence_intended_occupancy,
      residence_current_occupancy: otherBorrower.residence_current_occupancy,
      is_modified: true
    };
    setBorrowerEdit(borrower);
  }

  // Fields mirrored from the "same address as" source borrower.
  const SAME_ADDRESS_FIELDS = [
    "current_address",
    "current_address_2",
    "city",
    "state",
    "zip_code",
    "currently_renting_or_owning",
    "years_at_current_residence",
    "market_value",
    "rental_amount",
    "residence_disposition_status",
    "residence_intended_occupancy",
    "residence_current_occupancy"
  ];

  function renderBorrowerOption(borrower, index) {
    return (
      <option key={index} value={index}>
        {borrower_name(borrower)}
      </option>
    );
  }

  const currentlyRenting = borrowerEdit.currently_renting_or_owning === RENT;
  const currentlyOwning = borrowerEdit.currently_renting_or_owning === OWN;
  const marriedToOptions = [];
  const borrowerOptions = { none: "None" };

  for (let i = 0; i < borrowers.length; i++) {
    if (i !== borrowerIndex) {
      marriedToOptions.push(renderBorrowerOption(borrowers[i], i));
      if (borrowers[i].same_address_as !== borrowerIndex.toString()) {
        borrowerOptions[i.toString()] = borrowers[i]
          ? borrower_name(borrowers[i])
          : `Borrower ${i + 1}`;
      }
    }
  }

  const readOnlyAddress =
    borrowerEdit.same_address_as !== "none" &&
    borrowerEdit.same_address_as !== "";

  // Keep the mirrored fields in sync with the "same address as" source. The
  // copy in onChangeSameAddressAs only runs when the LO picks the option; an
  // IMPORTED co-borrower already has same_address_as set, so without this the
  // Market Value (and the new Status/Occupancy) never fill in from Borrower 1.
  const sameAddressSource = readOnlyAddress
    ? borrowers[parseInt(borrowerEdit.same_address_as, 10)]
    : null;

  useEffect(() => {
    if (!sameAddressSource) return;
    const changed = SAME_ADDRESS_FIELDS.some(
      f => borrowerEdit[f] !== sameAddressSource[f]
    );
    if (!changed) return;
    const mirror = {};
    SAME_ADDRESS_FIELDS.forEach(f => (mirror[f] = sameAddressSource[f]));
    setBorrowerEdit(be => ({ ...be, ...mirror }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerEdit.same_address_as, sameAddressSource]);

  return (
    <div className="edit-borrower">
      <Section name={`Borrower #${borrowerIndex + 1}`}>
        <div className="half-width">
          <TextField
            fieldName="first_name"
            name="Borrower"
            value={borrowerEdit.first_name}
            placeholder="First"
            onChange={onChangeBorrower}
            showRequired
            title="LANIS Training Tip: Use full Legal Name so you are consistent throughout the process. Use Middle initials where needed. Remember to double check the Tax Returns, W2's and 1099's for consistency. It's always a great idea to pull a Property Profile to see how they hold Title. Consistency in the names will also let you know what name to use when pulling a Credit Report. This can eliminate Change of Circumstances towards the end of the transaction when time may be critical."
          />
          <TextField
            fieldName="middle_name"
            value={borrowerEdit.middle_name}
            placeholder="Middle"
            onChange={onChangeBorrower}
          />
          <TextField
            fieldName="last_name"
            name="Last Name"
            value={borrowerEdit.last_name}
            placeholder="Last"
            onChange={onChangeBorrower}
            showRequired
          />
          <TextField
            fieldName="suffix"
            value={borrowerEdit.suffix}
            placeholder="Suffix"
            onChange={onChangeBorrower}
          />
          <TextField
            fieldName="current_address"
            name="Current Address"
            value={borrowerEdit.current_address}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />
          <TextField
            fieldName="current_address_2"
            name="Current Address 2"
            value={borrowerEdit.current_address_2}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />
          <TextField
            fieldName="city"
            name="City"
            value={borrowerEdit.city}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />
          <TextField
            fieldName="state"
            name="State"
            value={borrowerEdit.state}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />
          <TextField
            fieldName="zip_code"
            name="Zip Code"
            value={borrowerEdit.zip_code}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />
          <SelectField
            fieldName="currently_renting_or_owning"
            name="Currently Renting or Own Home"
            value={borrowerEdit.currently_renting_or_owning}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
            selectOptions={RENTING_OR_OWNING}
          />
          <NumberField
            fieldName="years_at_current_residence"
            name="No of Yrs"
            value={borrowerEdit.years_at_current_residence}
            onChange={onChangeBorrower}
            readOnly={readOnlyAddress}
          />

          {currentlyOwning && (
            <>
              <SelectField
                fieldName="residence_disposition_status"
                name="Status"
                value={borrowerEdit.residence_disposition_status}
                onChange={onChangeResidence}
                selectOptions={DISPOSITION_STATUS}
                readOnly={readOnlyAddress}
              />
              <SelectField
                fieldName="residence_intended_occupancy"
                name="Intended Occupancy"
                value={borrowerEdit.residence_intended_occupancy}
                onChange={onChangeResidence}
                selectOptions={OCCUPANCY}
                readOnly={readOnlyAddress}
              />
              <SelectField
                fieldName="residence_current_occupancy"
                name="Current Occupancy"
                value={borrowerEdit.residence_current_occupancy}
                onChange={onChangeBorrower}
                selectOptions={OCCUPANCY}
                readOnly={readOnlyAddress}
              />
            </>
          )}

          {currentlyOwning && subjectPropertyIsReo && (
            <CurrencyField
              fieldName="market_value"
              name="Market Value of Primary Residence"
              value={borrowerEdit.market_value}
              onChange={onChangeBorrower}
              readOnly={readOnlyAddress}
            />
          )}

          {currentlyRenting && (
            <CurrencyField
              fieldName="rental_amount"
              name="Rental Amount/Month"
              value={borrowerEdit.rental_amount}
              onChange={onChangeBorrower}
              readOnly={readOnlyAddress}
            />
          )}
          {currentlyRenting && (
            // TODO: Is this in months?
            <NumberField
              fieldName="how_long_in_current_residence"
              name="How Long in Current Residence"
              value={borrowerEdit.how_long_in_current_residence}
              onChange={onChangeBorrower}
            />
          )}

          {Object.keys(borrowerOptions).length > 1 && (
            <SelectField
              fieldName="same_address_as"
              name="Same address as..."
              selectOptions={borrowerOptions}
              value={borrowerEdit.same_address_as}
              onChange={onChangeSameAddressAs}
            />
          )}

          <SelectField
            fieldName="marital_status"
            name={`Marital Status Borrower ${borrowerIndex + 1}`}
            value={borrowerEdit.marital_status}
            onChange={onChangeBorrower}
            selectOptions={MARITAL_STATUS}
          />

          <SelectField
            fieldName="married_to"
            name="Married to"
            value={borrowerEdit.married_to}
            onChange={onChangeBorrower}
            showDefaultOption
          >
            <option value="Other">Other</option>
            {marriedToOptions}
          </SelectField>

          <TextField
            typeOverride="email"
            fieldName="email"
            name="Email"
            value={borrowerEdit.email}
            onChange={onChangeBorrower}
          />
          <PhoneField
            fieldName="cell_phone"
            name="Cell Phone"
            value={borrowerEdit.cell_phone}
            onChange={onChangeBorrower}
          />
          <PhoneField
            fieldName="home_phone"
            name="Home Phone"
            value={borrowerEdit.home_phone}
            onChange={onChangeBorrower}
          />
          <PhoneField
            fieldName="work_phone"
            name="Work Phone"
            value={borrowerEdit.work_phone}
            onChange={onChangeBorrower}
          />
          <SsnField
              fieldName="ssn"
              name="Social Security Number"
              value={borrowerEdit.ssn}
              onChange={onChangeBorrower}
              showRequired
              title="LANIS Training Tip: The Social Security Field must be filled in even though the Borrower may not want you to pull credit.  You can use a temporary SS# i.e., 111-11-1111 and fill in the other required fields then click on the Save Button below when it turns Blue."
          />
          <DateField
            fieldName="date_of_birth"
            name="DOB"
            value={borrowerEdit.date_of_birth}
            onChange={onChangeBorrower}
            showRequired
          />
          <SelectField
            fieldName="is_veteran"
            name="Is the Borrower a Veteran"
            value={borrowerEdit.is_veteran}
            onChange={onChangeBorrower}
            selectOptions={YES_NO_TBD}
          />

          {borrowerEdit.is_veteran === YES && (
            <a
              className="button inverse-button"
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.benefits.va.gov/homeloans/purchaseco_eligibility.asp"
            >
              Check Eligibility
            </a>
          )}
          <SelectField
            fieldName="on_title"
            name="Will the Borrower be on Title"
            value={borrowerEdit.on_title}
            onChange={onChangeBorrower}
            selectOptions={YES_NO_TBD_CREDIT_DEBT_OTHER}
          />
          {borrowerEdit.on_title && borrowerEdit.on_title !== YES && (
            <div className="input-field-container">
              <label htmlFor="on_title_note">
                Reason Borrower is not on Title?
              </label>
              <textarea
                id="on_title_note"
                className="input-field"
                rows={2}
                style={{ height: "auto", minHeight: "48px", resize: "vertical" }}
                spellCheck={true}
                placeholder="Notes:"
                value={borrowerEdit.on_title_note || ""}
                onChange={e =>
                  onChangeBorrower({ on_title_note: e.target.value })
                }
              />
            </div>
          )}

          <SelectField
            fieldName="on_loan"
            name="Will the Borrower be on the Loan"
            value={borrowerEdit.on_loan}
            onChange={onChangeBorrower}
            selectOptions={YES_NO_TBD_CREDIT_DEBT_OTHER}
          />
          {borrowerEdit.on_loan && borrowerEdit.on_loan !== YES && (
            <div className="input-field-container">
              <label htmlFor="on_loan_note">
                Reason Borrower is not on the Loan?
              </label>
              <textarea
                id="on_loan_note"
                className="input-field"
                rows={2}
                style={{ height: "auto", minHeight: "48px", resize: "vertical" }}
                spellCheck={true}
                placeholder="Notes:"
                value={borrowerEdit.on_loan_note || ""}
                onChange={e =>
                  onChangeBorrower({ on_loan_note: e.target.value })
                }
              />
            </div>
          )}
          <div
            style={{
              marginTop: "10px",
              marginBottom: "2px",
              fontWeight: 600
            }}
          >
            Credit Scores{" "}
            <TrainingTip content="LANIS Training Tip: Enter the three bureau scores from the credit report (Experian, TransUnion, Equifax), or they auto-fill when a credit report is imported. The Mid Score is the borrower's representative score — the median of the three (or the lower of two, or the single score). The LOWEST Mid Score across all borrowers flows to the Summary Sheet and drives loan pricing (LLPA). The range below is set automatically from the Mid Score, but you can still override it when there is no credit pull." />
          </div>
          <NumberField
            fieldName="credit_score_experian"
            name="Experian"
            value={borrowerEdit.credit_score_experian}
            onChange={onChangeBureauScore}
            decimalPlaces={0}
            blankZero
          />
          <NumberField
            fieldName="credit_score_transunion"
            name="TransUnion"
            value={borrowerEdit.credit_score_transunion}
            onChange={onChangeBureauScore}
            decimalPlaces={0}
            blankZero
          />
          <NumberField
            fieldName="credit_score_equifax"
            name="Equifax"
            value={borrowerEdit.credit_score_equifax}
            onChange={onChangeBureauScore}
            decimalPlaces={0}
            blankZero
          />
          <NumberField
            className="mid-score-field"
            name="Mid Score (auto)"
            value={borrowerEdit.credit_mid_score}
            decimalPlaces={0}
            readOnly
            blankZero
          />
          <SelectField
            fieldName="lowest_credit_mid_score"
            name="Lowest Credit Mid-Score (range)"
            value={borrowerEdit.lowest_credit_mid_score}
            onChange={onChangeBorrower}
            selectOptions={CREDIT_SCORE_RANGES}
            showDefaultOption
            showRequired
          />
          <div>
            <a
              className="button inverse-button"
              href="https://www.titleprofile.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Look up Current Property Profile
            </a>{" "}
            <TrainingTip content="LANIS Training Tip: You should look up the Property Profile of the property they are living in to see the History and if the Mortgages or Liens on the property would make it a short sale (if they are purchasing the property they are currently living in). Also, if there is anything recorded on the property such as a lien that could take time to resolve and be removed. Double check the City, and how Title was held by the Sellers vs. the Contract names. Get a Prelim Title Report as soon as possible or call your Title Company for a deep search of the property. If they currently own the property, it's still important to find out the details, i.e. if a NOD was ever filed on the property, because if they are buying another property and it's a Jumbo loan, they may not qualify. It is a good idea to have a complete understanding of the Borrower and the property they have and what they will be purchasing." />
          </div>
          <div>
            <a
              className="button inverse-button"
              href="https://www.realtor.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Look up Current Market Value
            </a>{" "}
            <TrainingTip content="LANIS Training Tip: You should look up the Market Value of the Subject Property to see if the Sales price matched the Comps for the home. If not, you may have an issue with the LTV/CLTV which will affect the Pricing of your loan. Also, if the Sales price of the home is Over Priced the Appraisal could come in low and there may not be a cure so your Loan will not go through. Talk with the Buyer’s Agent to see how to resolve it. Check the Borrowers’ reserves to see if they could come in with more funds to close." />
          </div>
        </div>
        <button className="button" onClick={cancel}>
          Cancel
        </button>
        <button className="button" disabled={!validBorrower()} onClick={save}>
          Save
        </button>
        <div className="required-field-note">* Required Field</div>
      </Section>

      {showRetainInvestmentNote && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "6px",
              maxWidth: "460px",
              padding: "22px 24px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.25)"
            }}
          >
            {showRetainInvestmentNote === SECONDARY ? (
              <p style={{ marginTop: 0, lineHeight: 1.5 }}>
                Since your Borrower is going to keep this home as a second home,
                go to the Second Home page (the button next to New Property) and
                fill in the mortgage payment and the costs not included in it.
                LANIS lists the home on Invest. REO as a second home: its full
                monthly cost goes into the DTI, and it needs 2 months of PITIA
                plus UPB reserves. On Debt Info, attach its mortgage to this home
                so the payment is not counted twice.
              </p>
            ) : (
              <p style={{ marginTop: 0, lineHeight: 1.5 }}>
                Since your Borrower is going to retain the property and make it an
                Investment property, you will need to go to the Investment page
                (the button next to New Property) and fill in the mortgage payment
                and the costs not included in it, to complete the PITI plus UPB
                reserve calculation. LANIS lists the home on Invest. REO. On Debt
                Info, attach its mortgage to this home so the payment is not
                counted twice. A total of all reserves required will be on the
                Reserves page.
              </p>
            )}
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
            >
              <button
                className="button"
                onClick={() => setShowRetainInvestmentNote(false)}
              >
                Yes, I understand
              </button>
              <button
                className="button inverse-button"
                onClick={() => setShowRetainInvestmentNote(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
